import * as React from 'react';
import CalendarAllDaySurfaceItem from './surfaceItems/CalendarAllDaySurfaceItem';
import CalendarConflictGroupItem from './surfaceItems/CalendarConflictGroupItem';
import CalendarDateHeader from './surfaceItems/CalendarDateHeader';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import CalendarSurfaceBlankItem from './surfaceItems/CalendarSurfaceBlankItem';
import CalendarSurfaceItem from './surfaceItems/CalendarSurfaceItem';
import CalendarSurfaceStore from './store/schema/CalendarSurfaceStore';
import getBaseTimeZone from './utils/getBaseTimeZone';
import { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { ConflictGroup } from './interfaces/ConflictGroup';
import { ContentHeader } from './surfaceItems/ContentHeader';
import { DisplayDate } from 'owa-datetime';
import { formatDay } from 'owa-datetime';
import { TemplateFunc } from './interfaces/SurfaceItem';

/**
 * This file defines templates we are exposing to consumers
 * Use SurfaceTemplates.tsx for internal templates not exposed to consumer
 */

/**
 * Default column template for week view
 * @param item: surface item
 * @param content: getter function for providing group content
 */
export let conflictGroupTemplate: TemplateFunc = (
    conflictGroup: ConflictGroup,
    content: () => JSX.Element[]
): JSX.Element => {
    return <CalendarConflictGroupItem key={conflictGroup.key} content={content} />;
};

/**
 * Default in-day item template
 * @param item: surface item
 * @param content: getter function for providing content
 * @param store: calendar surface store to pass user options
 */
export let inDayItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number,
    state?: any
): JSX.Element => {
    return (
        <CalendarSurfaceItem
            key={model.ItemId.Id}
            item={model}
            colorScheme={colorScheme}
            charmId={charmId}
            dimPastEvents={store.userOptions.dimPastEvents}
            disableTabIndex={store.disableTabbableSurfaceItems}
            itemState={{
                continuesLeft: false,
                continuesRight: false,
                timeZoneId: getBaseTimeZone(store.timeZoneIds),
            }}
        />
    );
};

/**
 * Default all-day item template
 * @param item: surface item
 * @param content: getter function for providing content
 */
export let allDayItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number,
    itemState?: any
): JSX.Element => {
    return (
        <CalendarAllDaySurfaceItem
            key={model.ItemId.Id}
            item={model}
            colorScheme={colorScheme}
            charmId={charmId}
            dimPastEvents={store.userOptions.dimPastEvents}
            disableTabIndex={store.disableTabbableSurfaceItems}
            itemState={itemState}
        />
    );
};

/**
 * Default blank item template
 * @param item: surface item
 * @param content: getter function for providing content
 */
export let blankItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    colorScheme: ColorScheme,
    itemState?: any
): JSX.Element => {
    return (
        <CalendarSurfaceBlankItem
            key={model.ItemId.Id}
            freeBusyType={model.FreeBusyType}
            colorScheme={colorScheme}
            dimPastEvents={store.userOptions.dimPastEvents}
            disableTabIndex={store.disableTabbableSurfaceItems}
            hideFreeBusyBar={false}
        />
    );
};

/**
 * Default template for date header
 * @param key: any unique key for react
 * @param displayDate: date header model
 */
export function createDateColumnHeader(
    displayDate: DisplayDate,
    key: number,
    style: React.CSSProperties,
    renderDateHeaderAddOn?: (date: DisplayDate) => JSX.Element,
    onSurfaceClick?: (
        start: DisplayDate,
        end: DisplayDate,
        isAllDay: boolean,
        props?: any,
        selectedCalendarId?: string
    ) => void,
    calendarFolderId?: string
) {
    return (
        <CalendarDateHeader
            key={key}
            displayDate={displayDate}
            formatHeader={formatDay}
            onSurfaceClick={onSurfaceClick}
            calendarFolderId={calendarFolderId}
            style={style}
            forwardedRef={domNode => {
                if (domNode) {
                    domNode.setAttribute('data-tabid', 'surfaceHeader_' + key);
                }
            }}>
            {!!renderDateHeaderAddOn && renderDateHeaderAddOn(displayDate)}
        </CalendarDateHeader>
    );
}

export let contentHeaderTemplate: (
    text: string,
    colorScheme: ColorScheme,
    onClick: () => void
) => JSX.Element = (text, colorScheme, onClick) => {
    return <ContentHeader text={text} colorScheme={colorScheme} onClick={onClick} />;
};

export let freeBusyTemplate: (className: string, colorScheme: ColorScheme) => JSX.Element = (
    className,
    colorScheme
) => {
    let style: React.CSSProperties = {
        backgroundColor: colorScheme.calPrimary,
    };
    return <div style={style} className={className} />;
};
